/**
 * Cliente HTTP del panel. Un solo origen (nginx/Vite hacen de proxy), así que
 * no hay CORS y la cookie de sesión viaja sola con `credentials: 'include'`.
 *
 * La API se alcanza bajo `/api` (el proxy le quita el prefijo). El flujo SSE va
 * por `/stream/live`, aparte (lib/live.ts).
 */

export class ErrorApi extends Error {
    constructor(
        public readonly status: number,
        mensaje: string,
        /**
         * El cuerpo del error, entero, cuando era JSON.
         *
         * Hasta la 012 solo sobrevivía el texto de `error`/`motivo` y **el resto
         * se tiraba**. Y hay errores cuyo valor está justo en el resto: un 409
         * de borrador dice además QUIÉN lo tocó y CUÁNDO, y sin eso quien lo
         * recibe no sabe si ha perdido su trabajo. Es el mismo fallo que ya
         * costó un alta entera —`bot-admin.ts` leía `err.issues` y devolvía solo
         * `err.error`—, en el otro extremo del mismo camino.
         *
         * `unknown` a propósito: quien lo lea tiene que mirar qué hay dentro.
         */
        public readonly cuerpo?: unknown,
    ) {
        super(mensaje)
        this.name = 'ErrorApi'
    }
}

async function pedir<T>(ruta: string, init?: RequestInit): Promise<T> {
    // ⚠️ El content-type SOLO cuando hay cuerpo. Mandar `application/json` con el
    // body vacío (p. ej. POST /auth/logout) hace que Fastify intente parsear ""
    // como JSON y responda 400. Se ve con el navegador, no con curl (que no manda
    // la cabecera si no hay -d).
    const headers: Record<string, string> = { ...(init?.headers as Record<string, string> | undefined) }
    if (init?.body !== undefined && headers['content-type'] === undefined) {
        headers['content-type'] = 'application/json'
    }
    const res = await fetch(`/api${ruta}`, { credentials: 'include', ...init, headers })
    if (!res.ok) {
        let detalle = `HTTP ${res.status}`
        let cuerpo: unknown
        try {
            const j = (await res.json()) as { motivo?: string; error?: string }
            cuerpo = j
            if (j.motivo) detalle = j.motivo
            else if (j.error) detalle = j.error
        } catch {
            /* cuerpo no JSON */
        }
        throw new ErrorApi(res.status, detalle, cuerpo)
    }
    return (await res.json()) as T
}

export const api = {
    get: <T>(ruta: string): Promise<T> => pedir<T>(ruta),
    post: <T>(ruta: string, cuerpo?: unknown): Promise<T> => {
        // No poner `body: undefined` (exactOptionalPropertyTypes lo rechaza).
        const init: RequestInit = { method: 'POST' }
        if (cuerpo !== undefined) init.body = JSON.stringify(cuerpo)
        return pedir<T>(ruta, init)
    },
    put: <T>(ruta: string, cuerpo?: unknown): Promise<T> => {
        const init: RequestInit = { method: 'PUT' }
        if (cuerpo !== undefined) init.body = JSON.stringify(cuerpo)
        return pedir<T>(ruta, init)
    },
    patch: <T>(ruta: string, cuerpo?: unknown): Promise<T> => {
        const init: RequestInit = { method: 'PATCH' }
        if (cuerpo !== undefined) init.body = JSON.stringify(cuerpo)
        return pedir<T>(ruta, init)
    },
    delete: <T>(ruta: string): Promise<T> => pedir<T>(ruta, { method: 'DELETE' }),
}
